import { CustomSelectItem } from '@hwm/ui-lib';
import { t } from '@lingui/macro';

export enum StandardAlarmConditionType {
  None = 'None', // 0
  LowAlarmLevel = 'LowAlarmLevel', // 50
  HighAlarmLevel = 'HighAlarmLevel', // 51
  LowOrHighAlarmLevel = 'LowOrHighAlarmLevel', // 52
  MinimumNightFlow = 'MinimumNightFlow', // 53
  RateOfChange = 'RateOfChange', // 54
  InsideBoundary = 'InsideBoundary', // 55
  LowerThanOtherChannel = 'LowerThanOtherChannel', // 56
  HigherThanOtherChannel = 'HigherThanOtherChannel', // 57
  TimeProfile = 'TimeProfile' // 58
}

export const StandardAlarmConditionTypeOptionsFn = (): Record<StandardAlarmConditionType, string> => ({
  [StandardAlarmConditionType.None]: t`None`,
  [StandardAlarmConditionType.LowAlarmLevel]: t`Low Alarm Level`,
  [StandardAlarmConditionType.HighAlarmLevel]: t`High Alarm Level`,
  [StandardAlarmConditionType.LowOrHighAlarmLevel]: t`Low or High Alarm Level`,
  [StandardAlarmConditionType.MinimumNightFlow]: t`Minimum Night Flow`,
  [StandardAlarmConditionType.RateOfChange]: t`Rate of Change`,
  [StandardAlarmConditionType.InsideBoundary]: t`Inside Low or High Alarm Level`,
  [StandardAlarmConditionType.LowerThanOtherChannel]: t`Value is lower than another channel`,
  [StandardAlarmConditionType.HigherThanOtherChannel]: t`Value is higher than another channel`,
  [StandardAlarmConditionType.TimeProfile]: t`Time Profile`,
});

export interface StandardAlarmLevelsModel {
  low?: number;
  high?: number;
  mnf?: number;
  roc?: number;
}

export const ProfileNumberOptionsFn = (): CustomSelectItem[] => [
  { label: t`Not Set`, value: null },
  { label: t`Profile 1`, value: AlarmProfiles.Profile1 },
  { label: t`Profile 2`, value: AlarmProfiles.Profile2 },
  { label: t`Profile 3`, value: AlarmProfiles.Profile3 },
  { label: t`Profile 4`, value: AlarmProfiles.Profile4 },
  { label: t`Profile 5`, value: AlarmProfiles.Profile5 },
  { label: t`Profile 6`, value: AlarmProfiles.Profile6 },
];

export type AlarmProfile =
  | 'profile1'
  | 'profile2'
  | 'profile3'
  | 'profile4'
  | 'profile5'
  | 'profile6';

export const AlarmProfiles = {
  Profile1: 'profile1' as AlarmProfile,
  Profile2: 'profile2' as AlarmProfile,
  Profile3: 'profile3' as AlarmProfile,
  Profile4: 'profile4' as AlarmProfile,
  Profile5: 'profile5' as AlarmProfile,
  Profile6: 'profile6' as AlarmProfile,
};

export const TimeProfileDaysOfWeek = {
  monday: 'monday',
  tuesday: 'tuesday',
  wednesday: 'wednesday',
  thursday: 'thursday',
  friday: 'friday',
  saturday: 'saturday',
  sunday: 'sunday',
} as const;

export type TimeProfileDaysOfWeekType =
  typeof TimeProfileDaysOfWeek[keyof typeof TimeProfileDaysOfWeek];

export const TimeProfileDaysOfWeekOptions: Record<TimeProfileDaysOfWeekType, string> = {
  [TimeProfileDaysOfWeek.monday]: t`Monday`,
  [TimeProfileDaysOfWeek.tuesday]: t`Tuesday`,
  [TimeProfileDaysOfWeek.wednesday]: t`Wednesday`,
  [TimeProfileDaysOfWeek.thursday]: t`Thursday`,
  [TimeProfileDaysOfWeek.friday]: t`Friday`,
  [TimeProfileDaysOfWeek.saturday]: t`Saturday`,
  [TimeProfileDaysOfWeek.sunday]: t`Sunday`,
};

export interface TimeProfileDataModel extends Record<TimeProfileDaysOfWeekType, TimeProfileDayEntry[]> {}

export interface TimeProfileDayEntry {
  time?: number; // minutes from midnight
  low: number;
  high: number;
}

export interface AlarmTimeProfiles {
  profile1?: TimeProfileDataModel;
  profile2?: TimeProfileDataModel;
  profile3?: TimeProfileDataModel;
  profile4?: TimeProfileDataModel;
  profile5?: TimeProfileDataModel;
  profile6?: TimeProfileDataModel;
}

export interface StandardAlarmOptionsModel {
  dualCallEnabled: boolean;
  dualCallIn: boolean;
  sendClear: boolean;
  midnightRepeat: boolean;
  eventDurationAlarmAvailable: boolean;
  eventDurationAlarmConditionNumber?: number;
}

export interface StandardAlarmConditionModel {
  alarmType: StandardAlarmConditionType;
  channelId?: number;
  profile?: AlarmProfile;
  otherChannelId?: number;
  value?: number;
  secondaryValue?: number;
  hysteresis?: number;
  persistenceX?: number;
  persistenceY?: number;
}

export interface StandardAlarmChannelOptionModel {
  id: number;
  name: string;
  unit: string;
}

// from GET
export interface SiteAlarmsStandardModel extends StandardAlarmsUpdateModel {
  supportedConditionTypes: StandardAlarmConditionType[];
  supportedChannels: StandardAlarmChannelOptionModel[];
  timeProfileSource?: AlarmTimeProfiles;
}

export interface StandardAlarmsUpdateModel {
  options: StandardAlarmOptionsModel;
  /**
   * Dictionary of fixed levels keyed by ChannelId. Null for Unilog
   */
  fixedLevels?: Record<number, StandardAlarmLevelsModel>;
  /**
   * Dictionary of AlarmConditions keyed by order (1-max_conditions)
   */
  conditions: Record<number, StandardAlarmConditionModel>;

  /**
   * Dictionary of units used on each channel where display units are different for input compared to stored units for alarm values.  Keyed by ChannelId
   * Populated by server - must be on response, but will not change while on client
   */
  channelDisplayUnits: Record<number, string>;

  /**
   * Time Profiles updates for Unilog only - should be null otherwise
   */
  timeProfiles?: AlarmTimeProfiles;
}

export interface SiteAlarmsStandardFormModel extends Omit<SiteAlarmsStandardModel, 'conditions'> {
  conditions: StandardAlarmConditionModel[];
}
